import { BookOpen, ExternalLink, Eye, EyeOff, Star, Tag } from "lucide-react";
import { useState } from "react";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  type AdminCourse,
  useAdminCourses,
  useSetCourseFeatured,
  useToggleCoursePublished,
} from "@/hooks/use-admin-courses";
import { JOB_ROLES } from "@/lib/roles";
import { SetCourseRolesDialog } from "./SetCourseRolesDialog";

function PublishedBadge({ published }: { published: boolean }) {
  return (
    <span
      className={
        published
          ? "inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-xs font-semibold text-green-700 ring-1 ring-green-600/20"
          : "inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground ring-1 ring-border"
      }
    >
      {published ? "Published" : "Unpublished"}
    </span>
  );
}

function RolesBadges({ targetRoles }: { targetRoles: string[] }) {
  if (targetRoles.length === 0) {
    return (
      <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground ring-1 ring-border">
        All
      </span>
    );
  }

  return (
    <div className="flex flex-wrap gap-1">
      {targetRoles.map((value) => {
        const role = JOB_ROLES.find((r) => r.value === value);
        const label = role ? role.label : value;
        return (
          <span
            key={value}
            className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary ring-1 ring-primary/20"
          >
            {label}
          </span>
        );
      })}
    </div>
  );
}

interface CourseRowProps {
  course: AdminCourse;
  onSetRoles: (course: AdminCourse) => void;
}

function CourseRow({ course, onSetRoles }: CourseRowProps) {
  const toggle = useToggleCoursePublished();
  const setFeatured = useSetCourseFeatured();

  return (
    <tr className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
      <td className="px-4 py-3">
        <div className="font-medium text-foreground">{course.title}</div>
        <div className="text-xs text-muted-foreground mt-0.5">{course.slug}</div>
      </td>
      <td className="px-4 py-3 text-sm text-muted-foreground">{course.totalModules} modules</td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap items-center gap-1.5">
          <PublishedBadge published={course.isPublished} />
          {course.isFeatured && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700 ring-1 ring-amber-400/30">
              <Star className="h-2.5 w-2.5 fill-amber-500 text-amber-500" />
              Featured
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-3">
        <RolesBadges targetRoles={course.targetRoles} />
      </td>
      <td className="px-4 py-3 text-right">
        <div className="inline-flex items-center gap-2">
          {course.previewUrl && (
            <a
              href={course.previewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted"
            >
              <ExternalLink className="h-3 w-3" />
              Preview
            </a>
          )}
          <button
            type="button"
            onClick={() => onSetRoles(course)}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted"
          >
            <Tag className="h-3 w-3" />
            Set Roles
          </button>
          {!course.isFeatured && (
            <button
              type="button"
              disabled={setFeatured.isPending}
              onClick={() => setFeatured.mutate(course.id)}
              className="inline-flex items-center gap-1.5 rounded-md border border-amber-300/50 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-700 transition-colors hover:bg-amber-100 disabled:opacity-50"
            >
              <Star className="h-3 w-3" />
              Set Featured
            </button>
          )}
          <button
            type="button"
            disabled={toggle.isPending}
            onClick={() => toggle.mutate({ courseId: course.id, isPublished: !course.isPublished })}
            className={
              course.isPublished
                ? "inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted disabled:opacity-50"
                : "inline-flex items-center gap-1.5 rounded-md border border-primary/30 bg-primary/5 px-3 py-1.5 text-xs font-medium text-primary transition-colors hover:bg-primary/10 disabled:opacity-50"
            }
          >
            {course.isPublished ? (
              <>
                <EyeOff className="h-3 w-3" />
                Unpublish
              </>
            ) : (
              <>
                <Eye className="h-3 w-3" />
                Publish
              </>
            )}
          </button>
        </div>
      </td>
    </tr>
  );
}

export function CoursesTab() {
  const { data: courses, isLoading, isError, error, refetch } = useAdminCourses();
  const [roleDialog, setRoleDialog] = useState<{
    open: boolean;
    course: AdminCourse | null;
    openCount: number;
  }>({
    open: false,
    course: null,
    openCount: 0,
  });

  function openRoleDialog(course: AdminCourse) {
    setRoleDialog((prev) => ({ open: true, course, openCount: prev.openCount + 1 }));
  }

  if (isError) {
    return (
      <ErrorState
        message={error instanceof Error ? error.message : "Failed to load courses"}
        onRetry={() => void refetch()}
      />
    );
  }

  return (
    <div className="space-y-6">
      <Card className="border-border/50">
        <CardHeader>
          <CardTitle
            style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 800, color: "#0f1944" }}
          >
            Course Catalog
          </CardTitle>
          <CardDescription>Control which courses are visible to users.</CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            headers={[
              { label: "Course" },
              { label: "Modules" },
              { label: "Status" },
              { label: "Roles" },
              { label: "Actions", className: "text-right whitespace-nowrap" },
            ]}
            loading={isLoading}
            empty={
              <EmptyState
                icon={BookOpen}
                title="No courses found"
                description="Courses are seeded on startup."
              />
            }
          >
            {courses?.map((course) => (
              <CourseRow key={course.id} course={course} onSetRoles={openRoleDialog} />
            ))}
          </DataTable>
        </CardContent>
      </Card>

      {roleDialog.course && (
        <SetCourseRolesDialog
          key={`${roleDialog.course.id}-${roleDialog.openCount}`}
          course={roleDialog.course}
          open={roleDialog.open}
          onOpenChange={(open) => setRoleDialog((prev) => ({ ...prev, open }))}
        />
      )}
    </div>
  );
}
